import { useMemo } from 'react';

import type { IIndirectTaxFilingV2AggregateResponse } from '@commenda/api/indirect-tax/filings';
import type { IIndirectTaxJurisdiction } from '@commenda/api/indirect-tax/registrations';
import {
  Badge,
  Button,
  DataTable,
  FilterConfig,
  Icons,
} from '@commenda/kakashi';
import { compareDatesChronologically } from '@commenda/utils';
import type { SortingState } from '@tanstack/react-table';

import { IndirectTaxRegion } from '../../common';
import { IndirectTaxFilingAccessHelper } from '../../common/helpers/IndirectTaxFilingAccessHelper';
import { useIndirectTaxFilingAccess } from '../../common/hooks/useIndirectTaxFilingAccess';
import { IndirectTaxTransactionHelper } from '../../transactions';
import { IndirectTaxFilingsHelper } from '../helpers';
import {
  IIndirectTaxFiling,
  IIndirectTaxFilingColumn,
  IIndirectTaxFilingColumnGroup,
  IndirectTaxFilingTableFilter,
} from '../types';

interface IndirectTaxFilingsTableCursorPagination {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onNextPage: () => void;
  onPreviousPage: () => void;
}

interface IndirectTaxFilingsTableProps {
  filings: IIndirectTaxFiling[];
  activeRegion: IndirectTaxRegion;
  activeFilters: Record<string, string | string[]>;
  onFilterChange: (filters: Record<string, string | string[]>) => void;
  regionJurisdictions: IIndirectTaxJurisdiction[];
  pagination: IndirectTaxFilingsTableCursorPagination;
  onRowClick?: (filing: IIndirectTaxFiling) => void;
  onApproveClick?: (filing: IIndirectTaxFiling) => void;
  selectable?: boolean;
  selectedFilingIds?: Set<string>;
  onSelectionChange?: (filings: IIndirectTaxFiling[]) => void;
  /**
   * Optional initial (mutable) sort applied on first render. Passing only the
   * sorting state leaves the table uncontrolled, so users can still re-sort by
   * clicking headers. Omit to keep the server-provided order.
   */
  defaultSorting?: SortingState;
  /**
   * Server-aggregated summary totals for the cards above the table.
   * Null when the active region is ALL_REGIONS (the backend refuses
   * cross-region aggregation; the UI hides cards in that case) or while
   * the first request is in flight without a previous cache hit.
   */
  aggregate: IIndirectTaxFilingV2AggregateResponse | null;
  /**
   * Adds the "Filed by" column, naming the agent who submitted the return.
   * Agent-facing surfaces only — clients have no use for internal agent names.
   */
  showFiledBy?: boolean;
}

const FILINGS_SEARCH_COLUMNS = [
  'jurisdiction',
  'returnName',
  'filingPeriod',
  'filingFrequency',
  'dueDate',
];

const FILED_BY_COLUMN: IIndirectTaxFilingColumn = {
  accessorKey: 'filedByAgentName',
  header: 'Filed by',
  title: 'Filed by',
  enableSorting: false,
  cell: ({ row }) =>
    row.original.filedByAgentName ? (
      <span className="txt-small-regular text-text-primary">
        {row.original.filedByAgentName}
      </span>
    ) : (
      <span className="txt-small-regular text-text-quaternary">—</span>
    ),
};

const IndirectTaxFilingsTable = ({
  filings,
  activeRegion,
  activeFilters,
  onFilterChange,
  regionJurisdictions,
  pagination,
  onRowClick,
  onApproveClick,
  selectable,
  selectedFilingIds,
  onSelectionChange,
  defaultSorting,
  aggregate,
  showFiledBy = false,
}: IndirectTaxFilingsTableProps) => {
  const { isInternalUser, isCalendarOnly } =
    useIndirectTaxFilingAccess(activeRegion);
  const columnGroups = useMemo((): IIndirectTaxFilingColumnGroup[] => {
    const groups: IIndirectTaxFilingColumnGroup[] = [
      {
        id: 'filing-info',
        columns: [
          {
            accessorKey: 'jurisdiction',
            header: 'Jurisdiction',
            title: 'Jurisdiction',
            enableSorting: true,
            sort: {
              shouldSort: true,
              sortingFn: (a: string, b: string) => a.localeCompare(b),
              ui: {
                asc: {
                  columnNode: 'A to Z',
                  globalNode: 'Jurisdiction - A to Z',
                },
                desc: {
                  columnNode: 'Z to A',
                  globalNode: 'Jurisdiction - Z to A',
                },
              },
            },
            cell: ({ row }) => (
              <span className="txt-small-regular text-text-primary">
                {row.original.jurisdiction}
              </span>
            ),
          },
          {
            accessorKey: 'status',
            header: 'Filing status',
            title: 'Filing status',
            enableSorting: false,
            cell: ({ row }) => {
              const config = IndirectTaxFilingsHelper.getStatusBadgeConfig(
                row.original.status
              );
              return (
                <Badge variant={config.variant} size="sm">
                  {config.label}
                </Badge>
              );
            },
          },
          {
            accessorKey: 'returnName',
            header: 'Return',
            title: 'Return',
            showInSelector: false,
            enableSorting: true,
            sort: {
              shouldSort: true,
              sortingFn: (a: string, b: string) => a.localeCompare(b),
              ui: {
                asc: { columnNode: 'A to Z', globalNode: 'Return - A to Z' },
                desc: { columnNode: 'Z to A', globalNode: 'Return - Z to A' },
              },
            },
            cell: ({ row }) => (
              <span className="txt-small-medium text-text-primary">
                {row.original.returnName}
              </span>
            ),
          },
          {
            accessorKey: 'filingPeriod',
            header: 'Filing period',
            title: 'Filing period',
            enableSorting: false,
            cell: ({ row }) => (
              <span className="txt-small-regular text-text-primary">
                {row.original.filingPeriod}
              </span>
            ),
          },
          {
            accessorKey: 'filingFrequency',
            header: 'Filing frequency',
            title: 'Filing frequency',
            enableSorting: false,
            cell: ({ row }) => (
              <span className="txt-small-regular text-text-primary">
                {IndirectTaxFilingsHelper.getFilingFrequencyName(
                  row.original.filingFrequency
                )}
              </span>
            ),
          },
          {
            accessorKey: 'dueDate',
            header: 'Due date',
            title: 'Due date',
            enableSorting: true,
            sort: {
              shouldSort: true,
              sortingFn: compareDatesChronologically,
              ui: {
                asc: {
                  columnNode: 'Oldest first',
                  globalNode: 'Due date - Oldest first',
                },
                desc: {
                  columnNode: 'Newest first',
                  globalNode: 'Due date - Newest first',
                },
              },
            },
            cell: ({ row }) => (
              <span className="txt-small-regular text-text-primary">
                {row.original.dueDate}
              </span>
            ),
          },
          ...(showFiledBy ? [FILED_BY_COLUMN] : []),
        ],
      },
      {
        id: 'filing-amount',
        columns: [
          {
            accessorKey: 'amount',
            header: 'Estimated Tax',
            title: 'Estimated Tax',
            enableSorting: true,
            align: 'right',
            headerClassName: 'w-full text-right',
            sort: {
              shouldSort: true,
              sortingFn: (a: string, b: string) => Number(a) - Number(b),
              ui: {
                asc: {
                  columnNode: 'Low to high',
                  globalNode: 'Estimated Tax - Low to high',
                },
                desc: {
                  columnNode: 'High to low',
                  globalNode: 'Estimated Tax - High to low',
                },
              },
            },
            cell: ({ row }) => {
              if (
                IndirectTaxFilingAccessHelper.isCalendarOnly(
                  row.original.region,
                  isInternalUser
                )
              ) {
                return (
                  <span className="txt-small-regular text-text-secondary">
                    Calendar tracking only
                  </span>
                );
              }
              if (
                row.original.hasGeneratedFilingReport === false ||
                !row.original.currency ||
                row.original.amount == null
              ) {
                return (
                  <span className="txt-small-regular text-text-quaternary">
                    N/A
                  </span>
                );
              }
              return (
                <div className="flex items-center justify-end gap-xs">
                  <span className="txt-small-regular text-text-quaternary">
                    {IndirectTaxTransactionHelper.getCurrencySymbol(
                      row.original.currency
                    )}
                  </span>
                  <span className="txt-small-regular text-text-primary">
                    {IndirectTaxTransactionHelper.formatCurrency(
                      row.original.amount
                    )}
                  </span>
                </div>
              );
            },
          },
        ],
      },
    ];

    if (onApproveClick) {
      groups.push({
        id: 'filing-actions',
        sticky: 'right',
        columns: [
          {
            accessorKey: 'approve',
            header: '',
            title: '',
            width: 120,
            showInSelector: false,
            enableSorting: false,
            align: 'right' as const,
            cell: ({ row }: { row: { original: IIndirectTaxFiling } }) => {
              if (
                !row.original.canApprove ||
                !IndirectTaxFilingAccessHelper.canViewDetails(
                  row.original.region,
                  isInternalUser
                )
              )
                return null;
              return (
                <div className="flex items-center justify-end">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={(event) => {
                      event.stopPropagation();
                      onApproveClick(row.original);
                    }}
                  >
                    Approve
                  </Button>
                </div>
              );
            },
          },
        ],
      });
    }

    return isCalendarOnly
      ? groups.filter((group) => group.id === 'filing-info')
      : groups;
  }, [onApproveClick, showFiledBy, isInternalUser, isCalendarOnly]);

  // Summary cards are server-aggregated (POST /filings-v2/aggregate) so they
  // stay correct across all matching filings, not just the visible page. The
  // helper owns the per-region label + currency-symbol patching so call sites
  // get ready-to-render items with no post-call domain logic. Returns
  // undefined for ALL_REGIONS so the cards drop entirely.
  const summaryConfig = useMemo(
    () =>
      isCalendarOnly
        ? undefined
        : IndirectTaxFilingsHelper.computeAggregateSummaryItems(
            activeRegion,
            aggregate
          ),
    [activeRegion, aggregate, isCalendarOnly]
  );

  const filterConfig = useMemo((): FilterConfig => {
    const supportsJurisdictionFilter =
      IndirectTaxFilingsHelper.regionSupportsJurisdictionFilter(activeRegion);

    const jurisdictionOptions = regionJurisdictions
      .slice()
      .sort((a, b) => a.jurisdictionName.localeCompare(b.jurisdictionName))
      .map((j) => ({
        label: IndirectTaxFilingsHelper.formatJurisdictionName(
          j.jurisdictionName
        ),
        value: j.jurisdictionId,
      }));

    const frequencyOptions =
      IndirectTaxFilingsHelper.getFrequencyFilterOptions();

    const filters: FilterConfig['filters'] = [];

    if (supportsJurisdictionFilter && jurisdictionOptions.length > 0) {
      filters.push({
        columnId: IndirectTaxFilingTableFilter.JURISDICTION,
        type: 'MULTI_SELECT',
        icon: <Icons.Globe04 className="size-4" />,
        label: 'Jurisdiction',
        options: jurisdictionOptions,
        showSearch: true,
        placeholder: 'Search jurisdictions',
      });
    }

    filters.push({
      columnId: IndirectTaxFilingTableFilter.FILING_FREQUENCY,
      type: 'MULTI_SELECT',
      icon: <Icons.Calendar className="size-4" />,
      label: 'Filing frequency',
      options: frequencyOptions,
    });

    filters.push({
      columnId: IndirectTaxFilingTableFilter.DUE_DATE,
      type: 'DATE',
      icon: <Icons.Calendar className="size-4" />,
      label: 'Due date',
    });

    return {
      filters,
      activeFilters,
      onFilterChange,
    };
  }, [activeRegion, regionJurisdictions, activeFilters, onFilterChange]);

  return (
    <DataTable
      data={filings}
      columnGroups={columnGroups}
      summaryConfig={summaryConfig}
      searchConfig={{
        isEnabled: true,
        columnsToEnableSearchFor: FILINGS_SEARCH_COLUMNS,
        placeholder: 'Search filings',
        debounceMs: 150,
      }}
      tableViewConfig={{
        isEnabled: false,
        defaultView: 'expanded',
      }}
      filterConfig={filterConfig}
      tableSortConfig={
        defaultSorting ? { sortingState: defaultSorting } : undefined
      }
      paginationConfig={{
        isEnabled: true,
        type: 'CURSOR',
        pageSize: pagination.pageSize,
        hasNextPage: pagination.hasNextPage,
        hasPreviousPage: pagination.hasPreviousPage,
        onNextPage: pagination.onNextPage,
        onPreviousPage: pagination.onPreviousPage,
        totalItems: pagination.totalItems,
        currentPage: pagination.currentPage,
      }}
      tableRowClassName="h-[52px]"
      onRowClick={
        onRowClick &&
        IndirectTaxFilingAccessHelper.canViewDetails(
          activeRegion,
          isInternalUser
        )
          ? (filing) => {
              if (
                IndirectTaxFilingAccessHelper.canViewDetails(
                  filing.region,
                  isInternalUser
                )
              )
                onRowClick(filing);
            }
          : undefined
      }
      rowClassName={(filing) =>
        IndirectTaxFilingAccessHelper.canViewDetails(
          filing.region,
          isInternalUser
        )
          ? ''
          : 'cursor-default'
      }
      rowDataAttributes={(row) => ({
        'data-ai-item': 'Filing',
        ...(row.id ? { 'data-ai-id': String(row.id) } : {}),
      })}
      rowSelectionConfig={
        selectable
          ? {
              isEnabled: true,
              selectionMode: 'multiple',
              getRowId: (row) => (row as IIndirectTaxFiling).id,
              onSelectionChange: (rows) =>
                onSelectionChange?.(rows as IIndirectTaxFiling[]),
              isRowSelected: (row) =>
                !!selectedFilingIds?.has((row as IIndirectTaxFiling).id),
              shouldShowRowSelectActionMenu: false,
            }
          : undefined
      }
    />
  );
};

export { IndirectTaxFilingsTable };
